import type { Dispatch, RefObject, SetStateAction } from 'react';
import {
    AlertTriangle,
    CalendarClock,
    CalendarRange,
    CheckCircle2,
    FileCheck2,
    Layers,
    Loader2,
    PenLine,
    RefreshCw,
    Search,
    X,
} from 'lucide-react';
import type { Invoice } from '../../types';
import { Button } from '../Button';
import type { OpenMonthBucket } from '../../services/invoiceLogic';
import { formatCents } from '../../services/money';
import { isProjectFinished, type ProjectStatusSource } from '../../services/projectBilling';
import { isListNavKey, type ListNavKey } from '../../services/listKeyboardNav';
import {
    awaitsCoverageCheck,
    billingModeLabel,
    consumesBillingMode,
    type BillingStance,
} from '../../services/billingMode';
import {
    EMPTY_DOCUMENT_TALLY,
    type ProjectDocumentTally,
} from '../../services/projectDocumentTally';
import type { FlatMonthSuggestion } from '../../services/flatRateBooking';
import {
    dayLabel,
    describePeriod,
    DOC_TYPE_CHOICES,
    DOCUMENT_TYPE_FILTERS,
    monthLabel,
    type CreatableDocumentType,
} from './invoicesViewShared';
import type { PickerStock, PickerStockLoad } from './pickerStockLogic';
import type { UndatedTotal } from './backlogDatingLogic';

/**
 * Der Anlagedialog "Neuer Beleg": die Projektliste mit Suche und
 * Tastaturbedienung, darueber Belegart, Leistungszeitraum, Zaehler und die
 * Kaesten zur Protokolluecke.
 *
 * Aus InvoicesView herausgeloest. Reine Anzeige: Zustand, Ableitungen und
 * Schreibwege bleiben in der Ansicht (bzw. in useBacklogDating) und kommen als
 * Props herein. Die Ansicht montiert den Dialog nur, solange er offen ist.
 *
 * Generisch ueber die Zeile: die Projektzeilen der Ansicht (projectRows) haben
 * einen grossen, abgeleiteten Typ. Hier steht nur, was der Dialog an ihnen
 * liest (PickerDialogRow); die Rueckrufe bekommen die Zeile der Ansicht
 * unveraendert zurueck.
 */

/** Das Projekt einer Zeile, soweit der Dialog es liest. */
export interface PickerDialogProject extends ProjectStatusSource {
    id: string;
    name?: string | null;
    project_number?: string | null;
}

/** Was der Dialog an einer Projektzeile liest - und nur das. */
export interface PickerDialogRow {
    project: PickerDialogProject;
    /** Offene Positionen der Zeile; gelesen wird nur ihre Anzahl. */
    positions: readonly unknown[];
    /** Offener Bestand je Leistungsmonat (openByMonth). */
    months: readonly OpenMonthBucket[];
    /** MONATSPAUSCHALE: noch nicht gebuchte Monate (suggestFlatMonths). */
    flatSuggestions: FlatMonthSuggestion[];
    /** Die ganze Protokolluecke, netto. */
    backlogNetCents: number;
    /** Davon noch nicht abgerechnet. */
    backlogOpenNetCents: number;
}

/** Womit der Buchungsdialog der Monatspauschale geoeffnet wird (flatBooking). */
export interface PickerFlatBooking {
    projectId: string;
    projectName: string;
    suggestions: FlatMonthSuggestion[];
}

/**
 * Die Props des Anlagedialogs.
 *
 * Jeder Name ist genau der Bezeichner, unter dem der Wert in InvoicesView
 * steht: ein spaeterer Schritt zieht den Zustand des Dialogs in einen Hook,
 * dessen Rueckgabe hier unmittelbar hineingespreizt wird.
 */
export interface ProjectPickerDialogProps<R extends PickerDialogRow> {
    /** Projekt, an dem gerade ein Beleg entsteht; `null`, wenn keines. */
    creating: string | null;
    /** Beleg, der gerade geoeffnet wird; `null`, wenn keiner. */
    opening: string | null;
    /** Eine Rueckfrage-Kette laeuft: im Dialog schreibt dann nichts mehr. */
    pickerLocked: boolean;
    closePicker: () => void;
    /** Meldung im Dialog (Projekt ohne Kunden, gescheitertes Anlegen ...). */
    error: string | null;
    setError: Dispatch<SetStateAction<string | null>>;

    /** Belegart und Leistungszeitraum: die Angaben, die die LISTE steuern. */
    docType: CreatableDocumentType;
    setDocType: Dispatch<SetStateAction<CreatableDocumentType>>;
    projectSearch: string;
    setProjectSearch: Dispatch<SetStateAction<string>>;
    periodFrom: string;
    setPeriodFrom: Dispatch<SetStateAction<string>>;
    periodTo: string;
    setPeriodTo: Dispatch<SetStateAction<string>>;
    includeUndated: boolean;
    setIncludeUndated: Dispatch<SetStateAction<boolean>>;

    /** Suchfeld und Scrollflaeche fuer die Tastatur (movePickerFocus). */
    pickerSearchRef: RefObject<HTMLInputElement | null>;
    pickerListRef: RefObject<HTMLDivElement | null>;
    movePickerFocus: (key: ListNavKey) => void;

    /** Belege mit Positionen; `null`, solange der Bestand fehlt. */
    pickerInvoices: readonly Invoice[] | null;
    pickerLoad: PickerStockLoad;
    pickerStock: PickerStock;
    /** Die angezeigten Zeilen: gefiltert nach Belegart, Betrag und Suche. */
    pickerProjects: readonly R[];
    pickerTotalNetCents: number;
    documentTallyByProject: ReadonlyMap<string, ProjectDocumentTally>;
    stanceFor: (projectId: string) => BillingStance;
    monthInPeriod: (month: string | null) => boolean;
    relevantNetCents: (row: R) => number;
    handleProjectClick: (entry: R) => void;
    openInvoice: (id: string) => Promise<void>;
    setFlatBooking: Dispatch<SetStateAction<PickerFlatBooking | null>>;
    /** Fehlt der Rueckruf, ist der Projektname im Kasten der Luecke nicht anklickbar. */
    onOpenProject?: (project: R['project']) => void;

    /** Protokolluecke: Zustand und Schreibwege aus useBacklogDating. */
    undatedTotal: UndatedTotal;
    autoDatableRows: readonly R[];
    manualUndatedRows: readonly R[];
    obviousBusy: boolean;
    backlogBusy: string | null;
    backlogNote: string | null;
    backlogError: string | null;
    backlogDateFor: (row: R) => string;
    setBacklogDates: Dispatch<SetStateAction<Record<string, string>>>;
    handleDateObviousRows: () => Promise<void>;
    handleDateBacklog: (row: R) => Promise<void>;
}

/** Der Dialog selbst: Abdunklung, Kopf, Meldung und die Projektliste. */
export function ProjectPickerDialog<R extends PickerDialogRow>(props: ProjectPickerDialogProps<R>) {
    const { creating, opening, pickerLocked, closePicker, error, setError } = props;
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-3 sm:p-4"
            role="presentation"
            // Klick auf die Flaeche neben dem Dialog schliesst ihn — der
            // gewohnte Weg neben Escape. Nur auf dem Overlay selbst
            // (target === currentTarget), sonst schloesse auch das
            // Loslassen einer Textmarkierung, die im Dialog begann.
            // Waehrend eines laufenden Anlegens bleibt er stehen.
            onMouseDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (creating != null || opening != null) return;
                // Waehrend einer Rueckfrage gehoert der Klick der
                // Rueckfrage — der Dialog dahinter darf nicht unter ihr
                // wegfallen.
                if (pickerLocked) return;
                closePicker();
            }}
        >
            {/* Feste Hoehe statt max-h: die beiden Spalten brauchen ein
                bekanntes Mass, sonst waechst die rechte Liste ueber den
                Bildschirm hinaus, statt in sich zu scrollen. 94vh sind
                auf einem 1366x768-Laptop rund 720 px und lassen mit dem
                Rand von 12 px den Rahmen des Overlays stehen.

                Die Breite ist nicht mehr auf 72rem gedeckelt: die
                Projektzeile traegt Name, Nummer, Belegzahlen,
                Monats-Chips und Betrag, und die brachen dort in vier
                Zeilen um. 96rem geben der Liste rund 380 px mehr; auf
                einem 1366er Laptop greift der Deckel ohnehin nicht,
                dort begrenzt w-full auf die Bildschirmbreite. */}
            <div
                className="flex h-[94vh] w-full max-w-[96rem] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5"
                role="dialog"
                aria-modal="true"
                aria-labelledby="picker-title"
            >
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-6 py-4">
                    <div className="min-w-0">
                        <h2
                            id="picker-title"
                            className="text-lg font-semibold tracking-tight text-slate-900"
                        >
                            Neuer Beleg
                        </h2>
                        {/* Der Dialog hat nur noch EINE Aufgabe: das Projekt
                            finden. Alles, was den Beleg beschreibt, wird
                            danach gefragt — und nur das, was fuer dieses
                            Projekt offen ist. Oben stehen deshalb allein
                            die beiden Angaben, die die LISTE steuern. */}
                        <p className="mt-0.5 text-sm leading-snug text-slate-500">
                            Belegart und Zeitraum grenzen die Liste ein.{' '}
                            <strong>Projekt</strong> wählen — was der Beleg braucht, wird
                            danach gefragt.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={closePicker}
                        aria-label="Dialog schließen"
                        // Dieselben Waechter wie Escape und der Klick
                        // neben den Dialog. `pickerLocked` allein
                        // genuegt nicht: `runPickerFlow` raeumt die
                        // Kette ab, BEVOR das Anlegen beginnt — in
                        // dieser Spanne ist der Riegel wieder offen,
                        // und ein Klick hier schloss den Dialog,
                        // waehrend der Beleg entstand und der Editor
                        // aufsprang.
                        disabled={pickerLocked || creating != null || opening != null}
                        title="Schließen (Esc)"
                        className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                {/* Im Dialog, nicht im Seitenfluss: ein Projekt ohne Kunden,
                    "nichts mehr offen" und ein gescheitertes Anlegen laegen
                    sonst unsichtbar hinter dem Overlay. */}
                {error && (
                    <div className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-6 py-3 text-sm text-red-800">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span className="flex-1">{error}</span>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-600 hover:underline"
                        >
                            schließen
                        </button>
                    </div>
                )}

                {/* ── Die Projektliste, jetzt der ganze Dialog ────
                    Eine Spalte statt zweier. Die linke trug sechs
                    Schalter, die VOR der Wahl des Projekts eingestellt
                    werden mussten — und die meisten konnten die Frage
                    gar nicht beantworten, die sie stellten: die
                    Abrechnungsart folgt dem Projekt, die Abschlagshoehe
                    braucht dessen Auftragswert, der Abschlagsabzug
                    dessen Abschlaege. Sie sind Schritte der
                    Rueckfrage-Kette nach dem Klick geworden
                    (invoicePickerFlow). Geblieben ist, was die LISTE
                    steuert: Belegart und Leistungszeitraum.

                    min-w-0 ist Pflicht und nicht Kosmetik: ein
                    Flex-Kind darf ohne diese Angabe nicht unter die
                    Eigenbreite seines Inhalts schrumpfen. Auf einem
                    schmalen Bildschirm schob die Liste damit ihre
                    rechte Kante aus dem Dialog heraus — und weil der
                    Dialog overflow-hidden traegt, war der Betrag am
                    rechten Rand ersatzlos abgeschnitten, ohne
                    Scrollbalken, der darauf hingewiesen haette. */}
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                        {/* Kopfzeile der Liste. Bei zweihundert Projekten ist
                            sie das Einzige, was den Blick sortiert: wie viele,
                            wie viel zusammen, und ein Feld zum Eingrenzen.

                            Hier stehen auch Belegart und Leistungszeitraum, und
                            zwar genau deshalb: beide sind keine Eigenschaften des
                            Belegs, sondern Filter DIESER Liste. Die Belegart
                            bestimmt, welche Projekte ueberhaupt erscheinen; der
                            Zeitraum, welche Monats-Chips sie tragen, welcher
                            Betrag an der Zeile steht und was der Zaehler
                            zusammenzaehlt. Eine Eingabe, die die Liste
                            umschreibt, gehoert ueber die Liste — als eigene
                            Spalte daneben sah sie aus wie eine Eigenschaft des
                            Belegs. */}
                        <PickerListHeader {...props} />

                        {/* Die Scrollflaeche traegt die Tastaturbedienung:
                            der Tastendruck kommt vom fokussierten
                            Zeilenknopf darin nach oben durch, und hier
                            liegt auch der Ref, ueber den die Zeilen
                            gefunden werden. */}
                        <PickerProjectList {...props} />
                </div>
            </div>
        </div>
    );
}

// Die Teile des Dialogs. Sie bekommen alle seine Props und lesen davon, was
// sie brauchen. Sie stehen auf Modulebene und nicht im Dialog: eine dort
// definierte Komponente entstuende bei jedem Zeichnen neu und wuerde jedes
// Mal neu montiert.

/**
 * Kopfzeile der Liste: Belegart, Suche, Zaehler, Leistungszeitraum und die
 * Kaesten zur Protokolluecke.
 */
function PickerListHeader<R extends PickerDialogRow>({
    docType,
    setDocType,
    setError,
    pickerLocked,
    pickerSearchRef,
    projectSearch,
    setProjectSearch,
    movePickerFocus,
    pickerInvoices,
    pickerProjects,
    pickerTotalNetCents,
    periodFrom,
    setPeriodFrom,
    periodTo,
    setPeriodTo,
    undatedTotal,
    includeUndated,
    setIncludeUndated,
    autoDatableRows,
    obviousBusy,
    backlogBusy,
    handleDateObviousRows,
    manualUndatedRows,
    onOpenProject,
    closePicker,
    backlogDateFor,
    setBacklogDates,
    handleDateBacklog,
    backlogNote,
    backlogError,
}: ProjectPickerDialogProps<R>) {
    return (
        <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-3">
            {/* Belegart — die erste der beiden Angaben, die
                die Liste steuern: aus ihr folgt, welche
                Projekte ueberhaupt erscheinen (der Abschlag
                rechnet ueber den Auftragswert und braucht
                keine offene Menge) und welcher Betrag an der
                Zeile steht. Deshalb steht sie hier und nicht
                in der Rueckfrage-Kette.

                Als Reihe statt als Raster: in der schmalen
                linken Spalte brauchten vier Knoepfe zwei
                Zeilen, ueber der Liste passen sie in eine. */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Belegart
                </span>
                {DOC_TYPE_CHOICES.map((choice) => (
                    <button
                        key={choice.type}
                        type="button"
                        onClick={() => {
                            setDocType(choice.type);
                            setError(null);
                        }}
                        aria-pressed={docType === choice.type}
                        disabled={pickerLocked}
                        className={`rounded-lg border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                            docType === choice.type
                                ? 'border-emerald-500 bg-emerald-50 font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-500'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50'
                        }`}
                    >
                        {choice.label}
                    </button>
                ))}
            </div>
            <p className="mt-1.5 text-xs leading-snug text-slate-500">
                {DOC_TYPE_CHOICES.find((c) => c.type === docType)?.hint}
            </p>

            <div className="mt-2.5 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-2.5">
                <div className="relative min-w-[14rem] max-w-xl flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        ref={pickerSearchRef}
                        disabled={pickerLocked}
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        // Pfeil-ab fuehrt aus dem Suchfeld in die
                        // Liste — tippen und auswaehlen ohne
                        // Handwechsel zur Maus.
                        onKeyDown={(e) => {
                            if (e.key !== 'ArrowDown') return;
                            e.preventDefault();
                            movePickerFocus('ArrowDown');
                        }}
                        placeholder="Projekt oder Nummer suchen"
                        aria-label="Projekt oder Nummer suchen"
                        className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-9 text-sm text-slate-800 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                    {/* Zuruecksetzen ohne neunmal Rueckschritt —
                        der Dialog kommt aus der Buchhaltung mit
                        vorbelegter Projektnummer. */}
                    {projectSearch !== '' && (
                        <button
                            type="button"
                            onClick={() => {
                                setProjectSearch('');
                                pickerSearchRef.current?.focus();
                            }}
                            aria-label="Suche leeren"
                            disabled={pickerLocked}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                {/* Erst zaehlen, wenn gezaehlt werden kann: ohne die
                    Belegpositionen ist projectRows leer, und "0
                    Projekte · 0,00 €" waere eine Aussage, die so
                    nicht stimmt. */}
                {pickerInvoices !== null && (
                    <p className="ml-auto text-sm text-slate-500">
                        {pickerProjects.length}{' '}
                        {pickerProjects.length === 1 ? 'Projekt' : 'Projekte'}{' '}
                        ·{' '}
                        <span className="font-semibold tabular-nums text-slate-800">
                            {formatCents(pickerTotalNetCents)}
                        </span>{' '}
                        {/* Dieselbe Beschriftung wie an der
                            Projektzeile: die Summe folgt dem
                            Zeitraum, und der steht jetzt direkt
                            darunter — "netto offen" haette
                            daneben behauptet, sie sei der
                            gesamte offene Bestand. */}
                        {docType === 'ADVANCE_INVOICE'
                            ? 'Auftrag netto'
                            : periodFrom || periodTo
                              ? 'netto im Zeitraum'
                              : 'netto offen'}
                    </p>
                )}
            </div>

            {/* Der Abschlag kennt keinen Leistungszeitraum — er
                rechnet ueber den Auftragswert. Seine Hoehe bleibt
                links: sie ist eine Eigenschaft des Belegs und
                kein Filter der Liste.

                Die Beschriftungen stehen VOR den Feldern statt
                darueber — das haelt die Reihe einzeilig. Auf einem
                1366er Laptop bleiben der rechten Spalte rund
                880 px, in die alle drei Felder samt Knopf passen;
                wird es enger, bricht flex-wrap um, statt die
                Datumsfelder unlesbar zu stauchen. */}
            {docType !== 'ADVANCE_INVOICE' && (
                <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 pt-2.5">
                    <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                        <CalendarRange className="h-4 w-4 text-slate-400" />
                        Leistungszeitraum
                    </span>
                    <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        Monat
                        <input
                            type="month"
                            disabled={pickerLocked}
                            value={periodFrom.slice(0, 7)}
                            onChange={(e) => {
                                const [y, m] = e.target.value
                                    .split('-')
                                    .map(Number);
                                if (!y || !m) {
                                    setPeriodFrom('');
                                    setPeriodTo('');
                                    return;
                                }
                                // Tag 0 des Folgemonats ist dessen letzter Tag
                                // — spart eine Schaltjahr-Tabelle.
                                const last = new Date(Date.UTC(y, m, 0))
                                    .toISOString()
                                    .slice(0, 10);
                                setPeriodFrom(`${e.target.value}-01`);
                                setPeriodTo(last);
                            }}
                            className="w-[8.5rem] rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-normal normal-case text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                        />
                    </label>
                    <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        von
                        <input
                            type="date"
                            disabled={pickerLocked}
                            value={periodFrom}
                            onChange={(e) => setPeriodFrom(e.target.value)}
                            className="w-[8.5rem] rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-normal normal-case text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                        />
                    </label>
                    <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        bis
                        <input
                            type="date"
                            disabled={pickerLocked}
                            value={periodTo}
                            onChange={(e) => setPeriodTo(e.target.value)}
                            className="w-[8.5rem] rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-normal normal-case text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                        />
                    </label>
                    {(periodFrom || periodTo) && (
                        <button
                            type="button"
                            disabled={pickerLocked}
                            onClick={() => {
                                setPeriodFrom('');
                                setPeriodTo('');
                            }}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
                        >
                            Zeitraum aufheben
                        </button>
                    )}
                    {/* Der gewaehlte Zeitraum als Satz. Er stand
                        bisher im Ergebniskasten der linken Spalte;
                        mit ihr faellt der Kasten weg, dieser eine
                        Satz aber nicht — drei Datumsfelder sagen
                        nicht, ob am Ende ein sinnvolles Fenster
                        steht, und „offen" ist genauso ein Ergebnis
                        wie ein Monat. */}
                    <span className="basis-full text-[11px] leading-snug text-slate-500">
                        {describePeriod(periodFrom, periodTo)}
                    </span>
                </div>
            )}

            {/* Nur bei gesetztem Zeitraum. Ohne ihn gibt es nichts
                zu erklaeren — leere Felder heissen "alles", und
                genau das steht woertlich im Ergebniskasten unten
                links ("Ohne Zeitraum — alles, was offen ist."). Ein
                Dauertext ueber der Liste haette die Kopfzeile nur
                schwerer gemacht, ohne etwas hinzuzufuegen. */}
            {docType !== 'ADVANCE_INVOICE' && (periodFrom || periodTo) && (
                <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                    Übernommen wird nur, was im Leistungsprotokoll in diesem
                    Zeitraum steht. Die Liste darunter zeigt bereits diese
                    Mengen.
                </p>
            )}

            {/* Undatierte Mengen gehoeren zu keinem Monat und fallen
                sonst durch jedes Zeitfenster — dauerhaft. Sie hier zu
                benennen ist der Unterschied zwischen einer bewussten
                Entscheidung und einer Rechnungszeile, die einfach fehlt. */}
            {docType !== 'ADVANCE_INVOICE' &&
                (periodFrom || periodTo) &&
                undatedTotal.count > 0 && (
                    <div className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <p className="text-xs leading-snug text-amber-900">
                            <strong>
                                {undatedTotal.count}{' '}
                                {undatedTotal.count === 1
                                    ? 'offene Position'
                                    : 'offene Positionen'}{' '}
                                ({formatCents(undatedTotal.netCents)})
                            </strong>{' '}
                            {undatedTotal.count === 1 ? 'hat' : 'haben'}{' '}
                            keinen Eintrag im Leistungsprotokoll und damit
                            kein Datum. Diese Mengen erscheinen in{' '}
                            <em>keinem</em> Monat.
                        </p>
                        <label className="mt-1.5 flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-amber-900">
                            <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
                                disabled={pickerLocked}
                                checked={includeUndated}
                                onChange={(e) =>
                                    setIncludeUndated(e.target.checked)
                                }
                            />
                            <span>
                                Nur für diesen Beleg zuschlagen. Damit
                                erklärst du sie als in diesem Zeitraum
                                erbracht — prüfe das, bevor der Beleg zum
                                Kunden geht. Am Projekt ändert sich nichts:
                                beim nächsten Zeitraum fallen sie wieder
                                heraus.
                            </span>
                        </label>
                    </div>
                )}

            {/* Der Haken oben hilft EINEM Beleg. Die Menge bleibt
                undatiert, gehoert weiter zu keinem Monat und kippt
                kumulativ den naechsten Beleg in die Sperre, sobald die
                Grundlage unter die Summe der Vorbelege rutscht. Hier
                steht der dauerhafte Weg: die Luecke im
                Leistungsprotokoll schliessen.

                EIGENE Bedingung, nicht die des Kastens darueber: der
                haengt an der OFFENEN Teilmenge. Kumulativ ist "nichts
                offen" der Normalfall — und genau dort blockiert die
                Luecke trotzdem. */}
            {/* Die eindeutigen Faelle: ein Vorschlag, kein Automatismus.
                Frueher lief das beim Oeffnen des Dialogs von selbst und
                stempelte den, der die Ansicht offen hatte, als Erbringer
                der Leistung. Wer klickt, steht zu Recht darunter — wer
                nicht klickt, ueberlaesst es dem Nachtlauf. */}
            {docType !== 'ADVANCE_INVOICE' &&
                (periodFrom || periodTo) &&
                autoDatableRows.length > 0 && (
                    <div className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <p className="text-xs font-semibold text-amber-900">
                            {autoDatableRows.length} Projekt(e) mit
                            Protokolllücke und eindeutigem Datum
                        </p>
                        <p className="mt-0.5 text-[11px] leading-snug text-amber-800">
                            Der erste protokollierte Tag steht in den
                            eigenen Daten des Projekts — hier ist nichts zu
                            entscheiden. Abends trägt der Nachtlauf sie
                            selbst nach. Auf Klick geschieht es sofort,
                            dann unter Ihrem Namen.
                        </p>
                        <button
                            type="button"
                            className="mt-2 rounded border border-amber-400 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                            disabled={
                                pickerLocked ||
                                obviousBusy ||
                                backlogBusy !== null
                            }
                            onClick={() => void handleDateObviousRows()}
                        >
                            {obviousBusy
                                ? 'läuft …'
                                : `Alle ${autoDatableRows.length} nachtragen`}
                        </button>
                    </div>
                )}

            {docType !== 'ADVANCE_INVOICE' &&
                (periodFrom || periodTo) &&
                manualUndatedRows.length > 0 && (
                    <div className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <p className="text-xs font-semibold text-amber-900">
                            Dauerhaft lösen: Datum nachtragen
                        </p>
                        <p className="mt-0.5 text-[11px] leading-snug text-amber-800">
                            Trägt die fehlende Menge als Eintrag im
                            Leistungsprotokoll zu diesem Tag nach. Danach
                            erscheint sie in jedem Zeitraum, der ihn
                            enthält — auch auf späteren Belegen. Die Mengen
                            im Leistungsverzeichnis bleiben unverändert.
                            Vorgeschlagen ist der erste protokollierte Tag
                            des Projekts.
                        </p>
                        <ul className="mt-2 max-h-48 space-y-1.5 overflow-y-auto">
                            {manualUndatedRows.map((row) => {
                                // Zwei Lagen, die verschieden dringend
                                // sind: offenes Geld muss auf eine
                                // Rechnung, bereits Abgerechnetes braucht
                                // nur sein Datum. Beides in einer Summe
                                // liesse den Nutzer raten.
                                const offen = row.backlogOpenNetCents;
                                const berechnet =
                                    row.backlogNetCents - offen;
                                return (
                                    <li
                                        key={row.project.id}
                                        className="flex items-start justify-between gap-2 rounded border border-amber-200 bg-white/60 px-2 py-1.5 text-[11px] text-amber-900"
                                    >
                                        <div className="min-w-0 flex-1">
                                            {/* Anklickbar: wer entscheiden
                                                soll, welcher Tag stimmt,
                                                muss ins Projekt schauen
                                                koennen. Der Dialog geht
                                                dabei zu — der Sprung
                                                wechselt die Ansicht. */}
                                            <button
                                                type="button"
                                                className="max-w-full truncate text-left font-semibold text-amber-900 underline decoration-amber-400 underline-offset-2 hover:text-amber-950 disabled:no-underline"
                                                disabled={!onOpenProject}
                                                onClick={() => {
                                                    closePicker();
                                                    onOpenProject?.(
                                                        row.project,
                                                    );
                                                }}
                                                title="Projekt öffnen"
                                            >
                                                {row.project
                                                    .project_number
                                                    ? `${row.project.project_number} — `
                                                    : ''}
                                                {row.project.name}
                                            </button>
                                            <div className="mt-0.5">
                                                {formatCents(
                                                    row.backlogNetCents,
                                                )}{' '}
                                                ohne Datum
                                                {offen > 0 ? (
                                                    <>
                                                        {' '}
                                                        — davon{' '}
                                                        <strong>
                                                            {formatCents(
                                                                offen,
                                                            )}{' '}
                                                            noch nicht
                                                            abgerechnet
                                                        </strong>
                                                        {berechnet > 0
                                                            ? `, ${formatCents(berechnet)} bereits abgerechnet`
                                                            : ''}
                                                    </>
                                                ) : (
                                                    <>
                                                        {' '}
                                                        — vollständig
                                                        abgerechnet, das
                                                        Datieren ändert am
                                                        Geld nichts
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-1.5">
                                            <input
                                                type="date"
                                                className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[11px] text-slate-800"
                                                disabled={pickerLocked}
                                                value={backlogDateFor(row)}
                                                onChange={(e) =>
                                                    setBacklogDates(
                                                        (prev) => ({
                                                            ...prev,
                                                            [row.project
                                                                .id]:
                                                                e.target
                                                                    .value,
                                                        }),
                                                    )
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                                                disabled={
                                                    pickerLocked ||
                                                    backlogBusy !== null
                                                }
                                                onClick={() =>
                                                    void handleDateBacklog(
                                                        row,
                                                    )
                                                }
                                            >
                                                {backlogBusy ===
                                                row.project.id
                                                    ? 'läuft …'
                                                    : 'Datum setzen'}
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        {backlogNote && (
                            <p className="mt-1.5 text-[11px] font-medium text-emerald-800">
                                {backlogNote}
                            </p>
                        )}
                        {backlogError && (
                            <p className="mt-1.5 text-[11px] font-medium text-red-700">
                                {backlogError}
                            </p>
                        )}
                    </div>
                )}
        </div>
    );
}

/** Die Scrollflaeche: Ladezeile, leerer Zustand oder die Projektzeilen. */
function PickerProjectList<R extends PickerDialogRow>(props: ProjectPickerDialogProps<R>) {
    const {
        pickerListRef,
        movePickerFocus,
        pickerInvoices,
        pickerLoad,
        setError,
        pickerStock,
        pickerProjects,
        projectSearch,
        setProjectSearch,
        pickerSearchRef,
        periodFrom,
        setPeriodFrom,
        periodTo,
        setPeriodTo,
        docType,
        pickerLocked,
    } = props;
    return (
        <div
            ref={pickerListRef}
            onKeyDown={(e) => {
                if (!isListNavKey(e.key)) return;
                e.preventDefault();
                movePickerFocus(e.key);
            }}
            className="min-h-0 flex-1 overflow-y-auto bg-slate-50/40"
        >
            {/* Die Ladezeile haengt am Ladezustand und NICHT am
                fehlenden Bestand: sie darf nur laufen, solange
                wirklich eine Abfrage laeuft. Jeder andere
                Zustand ohne Bestand — auch ein hier nicht
                erwarteter — bekommt den Wiederholen-Knopf.
                Sonst genuegte ein einziger Weg, der den
                Bestand verwirft, ohne neu zu holen, und der
                Dialog stuende dauerhaft vor einem Ladebalken,
                hinter dem nichts mehr passiert. */}
            {pickerInvoices === null ? (
                pickerLoad === 'loading' ? (
                    <p className="flex items-center justify-center gap-2 p-12 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Offene
                        Mengen werden ermittelt …
                    </p>
                ) : (
                    <div className="mx-auto max-w-md p-12 text-center">
                        <span className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <AlertTriangle className="h-5 w-5" />
                        </span>
                        <p className="mb-4 text-sm leading-relaxed text-slate-600">
                            Ohne die bereits abgerechneten Mengen lässt
                            sich nicht sagen, was noch offen ist. Es wird
                            deshalb kein Projekt angeboten.
                        </p>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                // Die Meldung des letzten
                                // Versuchs mit wegnehmen: sie
                                // stuende sonst neben der
                                // laufenden Ladezeile.
                                setError(null);
                                pickerStock.retry();
                            }}
                        >
                            <RefreshCw className="h-4 w-4" /> Erneut
                            versuchen
                        </Button>
                    </div>
                )
            ) : pickerProjects.length === 0 ? (
                /* Der leere Zustand nennt nicht nur den Grund,
                   sondern traegt den Weg heraus als Knopf: der
                   Grund steht links im Dialog (Suche, Zeitraum),
                   und ihn dort wiederzufinden war der eigentliche
                   Aufwand. Die Knoepfe stellen nur Suchtext bzw.
                   Zeitraum zurueck — dieselben Setter wie die
                   Felder selbst. */
                <div className="mx-auto max-w-md p-12 text-center">
                    <span className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                        {projectSearch.trim() ? (
                            <Search className="h-5 w-5" />
                        ) : periodFrom || periodTo ? (
                            <CalendarRange className="h-5 w-5" />
                        ) : (
                            <CheckCircle2 className="h-5 w-5" />
                        )}
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                        {projectSearch.trim()
                            ? `Kein Projekt passt zu „${projectSearch.trim()}". Die Suche greift auf Name und Projektnummer.`
                            : docType === 'ADVANCE_INVOICE'
                              ? 'Es gibt kein Projekt mit hinterlegtem Auftragswert — ohne ihn fehlt einem Abschlag die Bemessungsgrundlage.'
                              : periodFrom || periodTo
                                ? 'In diesem Zeitraum ist nichts offen. Entweder wurde dort nichts geleistet, oder es ist bereits abgerechnet — heb den Zeitraum auf, um den gesamten offenen Bestand zu sehen.'
                                : 'Kein Projekt hat offene Positionen. Abgerechnet wird, was als erbracht erfasst und noch nicht in Rechnung gestellt ist.'}
                    </p>
                    {projectSearch.trim() ? (
                        <button
                            type="button"
                            // Auch hier: waehrend einer
                            // Rueckfrage schreibt im
                            // Anlagedialog nichts mehr. Der
                            // Knopf liegt zwar hinter dem
                            // Overlay, ist aber ohne Fokusfalle
                            // per Shift+Tab erreichbar.
                            disabled={pickerLocked}
                            onClick={() => {
                                setProjectSearch('');
                                pickerSearchRef.current?.focus();
                            }}
                            className="mt-4 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
                        >
                            Suche leeren
                        </button>
                    ) : (periodFrom || periodTo) &&
                      docType !== 'ADVANCE_INVOICE' ? (
                        <button
                            type="button"
                            disabled={pickerLocked}
                            onClick={() => {
                                setPeriodFrom('');
                                setPeriodTo('');
                            }}
                            className="mt-4 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
                        >
                            Zeitraum aufheben
                        </button>
                    ) : null}
                </div>
            ) : (
                <ul className="divide-y divide-slate-200/70 bg-white">
                    {pickerProjects.map((entry) => (
                        <PickerProjectRow key={entry.project.id} {...props} entry={entry} />
                    ))}
                </ul>
            )}
        </div>
    );
}

/** Eine Projektzeile samt den Knoepfen daneben (Entwurf, Monatspauschale). */
function PickerProjectRow<R extends PickerDialogRow>({
    entry,
    documentTallyByProject,
    creating,
    opening,
    pickerLocked,
    handleProjectClick,
    stanceFor,
    docType,
    periodFrom,
    periodTo,
    monthInPeriod,
    relevantNetCents,
    closePicker,
    openInvoice,
    setFlatBooking,
}: ProjectPickerDialogProps<R> & { entry: R }) {
    // Wie viele Belege dieses Projekt schon
    // hat. Aus `invoices` und nicht aus dem
    // Bestand des Dialogs: beide fuehren
    // dieselben Belege, `invoices` steht aber
    // schon, waehrend der Bestand noch geholt
    // wird — und nach dem Ausstellen ist es
    // dieselbe Liste, die aktualisiert wird.
    const tally =
        documentTallyByProject.get(entry.project.id) ??
        EMPTY_DOCUMENT_TALLY;
    const draft = tally.latestDraft;
    return (
        <li
            // Waehrend ein Beleg entsteht, treten
            // die uebrigen Zeilen zurueck statt
            // alle gemeinsam blass zu werden: so
            // sieht man, an WELCHER Zeile
            // gearbeitet wird.
            className={`flex items-stretch transition-colors ${
                creating != null &&
                creating !== entry.project.id
                    ? 'opacity-40'
                    : 'hover:bg-slate-50'
            }`}
        >
            <button
                data-picker-row=""
                onClick={() => handleProjectClick(entry)}
                // Waehrend einer laufenden
                // Rueckfrage-Kette gesperrt: die
                // Schritte liegen als Overlay
                // darueber, ohne Fokusfalle, und
                // der Fokus blieb nach dem Klick
                // auf dieser Zeile. Enter oder
                // Leertaste im offenen Schritt
                // startete die Kette von vorn und
                // warf die eben gegebene Antwort
                // kommentarlos weg. Auch die
                // Pfeiltasten uebergehen gesperrte
                // Zeilen (movePickerFocus).
                //
                // Und waehrend die Deckungspruefung
                // laeuft: bis sie steht, ist nicht
                // entschieden, ob dieses Projekt
                // ueberhaupt kumulativ darf.
                disabled={
                    creating != null ||
                    pickerLocked ||
                    awaitsCoverageCheck(
                        stanceFor(entry.project.id),
                        docType,
                    )
                }
                className="flex min-w-0 flex-1 items-start justify-between gap-5 rounded-none px-6 py-4 text-left outline-none focus-visible:bg-emerald-50/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500"
            >
                <span className="min-w-0">
                    <span className="block truncate text-[15px] font-semibold leading-tight text-slate-900">
                        {entry.project.name}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                        {entry.project.project_number}{' '}
                        · {entry.positions.length}{' '}
                        {entry.positions.length === 1
                            ? 'offene Position'
                            : 'offene Positionen'}
                    </span>
                    {/* Zwei getrennte Angaben, weil
                        sie zwei Fragen beantworten:
                        wie viel schon beim Kunden
                        ist (Historie) — und ob
                        jemand die offenen Mengen
                        bereits in einem Entwurf
                        beansprucht hat. Die zweite
                        verhindert den doppelten
                        Entwurf ueber dieselbe
                        Leistung.

                        NICHT verwechseln mit der
                        Abrechnungsart weiter unten:
                        `tally.issued` zaehlt JEDE
                        Belegart mit Nummer, auch
                        Abschlag, Storno und
                        Gutschrift. Ein Projekt mit
                        zwei ausgestellten Abschlaegen
                        steht hier mit „2 Belege" und
                        hat trotzdem kein
                        Abrechnungsmodell. Massgeblich
                        dafuer ist ausschliesslich
                        stanceFor. */}
                    {/* Nur noch EIN Kasten je Zeile:
                        die Belegzahl ist Alltag und
                        steht als stille Zeile, der
                        offene Entwurf ist die
                        Ausnahme und behaelt den
                        farbigen Chip. Vorher trugen
                        beide einen Rahmen, und die
                        Ausnahme sah aus wie der
                        Regelfall. */}
                    <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {/* Abgeschlossenes Projekt: die
                            naechste Rechnung ist seine
                            SCHLUSSRECHNUNG, und nur die
                            zieht die gestellten
                            Abschlaege ab. Aus der
                            Buchhaltung heraus ist die
                            Belegart schon gesetzt
                            (openPickerForProject) — wer
                            von hier kommt, sieht es an
                            der Zeile. Steht die Belegart
                            auf etwas anderes, wird das
                            danebengesagt statt still
                            uebergangen. */}
                        {isProjectFinished(entry.project) && (
                            <span className="inline-flex items-center gap-1.5 rounded border border-purple-200 bg-purple-100 px-1.5 py-0.5 text-[11px] font-bold uppercase leading-4 text-purple-700">
                                Schlussrechnung
                            </span>
                        )}
                        {isProjectFinished(entry.project) &&
                            docType !== 'FINAL_INVOICE' && (
                                <span className="inline-flex items-center gap-1 text-[11px] leading-4 text-amber-700">
                                    Belegart steht auf „
                                    {DOCUMENT_TYPE_FILTERS.find(
                                        ([t]) => t === docType,
                                    )?.[1] ?? docType}
                                    “
                                </span>
                            )}
                        {tally.issued > 0 ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
                                <FileCheck2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                                {tally.issued}{' '}
                                {tally.issued === 1
                                    ? 'Beleg'
                                    : 'Belege'}{' '}
                                ausgestellt
                                {tally.lastIssued && (
                                    <span className="text-slate-400">
                                        · zuletzt{' '}
                                        {
                                            tally
                                                .lastIssued
                                                .invoiceNumber
                                        }
                                        {tally
                                            .lastIssued
                                            .invoiceDate
                                            ? ` vom ${dayLabel(tally.lastIssued.invoiceDate)}`
                                            : ''}
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="text-[11px] leading-4 text-slate-400">
                                noch kein Beleg
                                ausgestellt
                            </span>
                        )}
                        {tally.drafts > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold leading-4 text-amber-800 ring-1 ring-inset ring-amber-300">
                                <PenLine className="h-3 w-3 shrink-0" />
                                {tally.drafts === 1
                                    ? '1 offener Entwurf'
                                    : `${tally.drafts} offene Entwürfe`}
                            </span>
                        )}
                    </span>
                    {/* Der offene Bestand ueber
                        die Monate. Ohne ihn
                        sieht man nur, was man
                        gerade nimmt, und nie,
                        was man stehen laesst.
                        Hervorgehoben ist, was
                        in den gewaehlten
                        Zeitraum faellt. */}
                    {docType !== 'ADVANCE_INVOICE' &&
                        entry.months.length > 0 && (
                            <span className="mt-2 flex flex-wrap items-center gap-1">
                                <span className="mr-0.5 text-[11px] text-slate-400">
                                    offen:
                                </span>
                                {/* Gekappt: bei einem
                                    langen Projekt
                                    waeren es zwanzig
                                    Monate. Chips statt
                                    Fliesstext, damit
                                    Monat und Betrag
                                    als Paar lesbar
                                    bleiben. */}
                                {entry.months
                                    .slice(0, 6)
                                    .map((bucket) => (
                                        <span
                                            key={
                                                bucket.month ??
                                                'ohne'
                                            }
                                            // Nur der
                                            // gewaehlte
                                            // Zeitraum
                                            // traegt
                                            // einen
                                            // Rahmen;
                                            // die
                                            // uebrigen
                                            // Monate
                                            // stehen
                                            // flach da
                                            // und
                                            // treten
                                            // damit
                                            // hinter
                                            // die
                                            // Auswahl
                                            // zurueck.
                                            className={`inline-flex items-baseline gap-1 rounded-md px-2 py-0.5 text-[11px] leading-5 ${
                                                monthInPeriod(
                                                    bucket.month,
                                                )
                                                    ? 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-300'
                                                    : 'bg-slate-100 text-slate-500'
                                            }`}
                                        >
                                            <span>
                                                {monthLabel(
                                                    bucket.month,
                                                )}
                                            </span>
                                            <span className="font-semibold tabular-nums">
                                                {formatCents(
                                                    bucket.netCents,
                                                )}
                                            </span>
                                        </span>
                                    ))}
                                {entry.months.length >
                                    6 && (
                                    <span className="text-[11px] text-slate-400">
                                        +{' '}
                                        {entry.months
                                            .length -
                                            6}{' '}
                                        weitere
                                    </span>
                                )}
                            </span>
                        )}
                    {/* Die Abrechnungsart dieses Projekts — in ALLEN drei
                        Zustaenden, nicht nur wenn etwas ueberstimmt wird.
                        Vorher stand hier eine Ansage nur bei Abweichung vom
                        Haken; an jeder anderen Zeile sah es deshalb so aus,
                        als gaelte der Haken links. Die Zeile SAGT nur an,
                        sie schaltet nichts: ein interaktives Element im
                        Zeilenknopf waere ungueltiges HTML und mit den
                        Pfeiltasten nicht erreichbar (movePickerFocus). */}
                    {consumesBillingMode(docType) &&
                        (() => {
                            const stance = stanceFor(entry.project.id);
                            const tone =
                                stance.kind === 'forced-progressive'
                                    ? 'border-amber-300 bg-amber-50 text-amber-900'
                                    : stance.kind === 'open'
                                      ? 'border-indigo-200 bg-indigo-50/70 text-indigo-900'
                                      : 'border-slate-200 bg-slate-50 text-slate-600';
                            // max-w: auf einem breiten Schirm liefe der Satz
                            // sonst ueber 1000 px und waere in einer Zeile
                            // nicht mehr zu lesen.
                            return (
                                <span
                                    className={`mt-2 flex max-w-3xl items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-xs leading-snug ${tone}`}
                                >
                                    <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    {stance.kind === 'established' ? (
                                        <span>
                                            Dieses Projekt rechnet{' '}
                                            <strong>
                                                {billingModeLabel(stance.fixedMode)}
                                            </strong>{' '}
                                            ab — der Beleg wird{' '}
                                            {billingModeLabel(stance.fixedMode)} angelegt.
                                            Das steht mit den bereits ausgestellten Belegen
                                            fest.
                                        </span>
                                    ) : stance.kind === 'forced-progressive' ? (
                                        <span>
                                            Für dieses Projekt sind{' '}
                                            {formatCents(stance.uncovered.gapCents)} netto
                                            als bereits abgerechnet ausgewiesen, ohne Beleg
                                            dazu in OS:IA — der Beleg wird{' '}
                                            <strong>fortschreibend</strong> angelegt.{' '}
                                            <em>Das ist vorläufig:</em> sobald die
                                            Altrechnung in der Buchhaltung unter
                                            „Vorrechnungen“ als Alt-Abschlag erfasst ist
                                            (oder die Alt-Positionen übernommen sind), lässt
                                            sich dieses Projekt auch kumulativ abrechnen.
                                        </span>
                                    ) : stance.kind === 'checking' ? (
                                        <span>
                                            Die Abrechnungsart steht noch nicht fest — die
                                            erfassten Alt-Abschläge werden gerade geprüft.
                                            Einen Augenblick.
                                        </span>
                                    ) : (
                                        <span>
                                            Noch kein Beleg, der eine Abrechnungsart
                                            festlegt — sie wird nach dem Klick gefragt.
                                            Dieser erste Beleg begründet das Modell des
                                            Projekts.
                                        </span>
                                    )}
                                </span>
                            );
                        })()}
                </span>
                {/* Beim Abschlag zaehlt der Auftragswert, nicht das
                    Offene: die Bemessungsgrundlage ist der
                    Gesamtauftrag. Den offenen Betrag hier zu zeigen
                    liesse den Abschlag falsch aussehen. */}
                <span className="shrink-0 text-right">
                    <span className="block text-base font-semibold tabular-nums leading-tight text-slate-900">
                        {formatCents(
                            relevantNetCents(entry),
                        )}
                    </span>
                    {/* Die Beschriftung sagt, WELCHE
                        Zahl das ist. "netto" allein
                        liess offen, ob der Zeitraum
                        schon eingerechnet war — und
                        genau daran ist die Zahl
                        vorher missverstanden worden. */}
                    <span className="mt-0.5 block text-[11px] text-slate-400">
                        {docType === 'ADVANCE_INVOICE'
                            ? 'Auftrag netto'
                            : periodFrom || periodTo
                              ? 'netto im Zeitraum'
                              : 'netto offen'}
                    </span>
                    {/* Beim Anlegen liegen Abfrage
                        und Editor dazwischen. Ein
                        Rad allein liess offen, ob
                        es der eigene Klick war —
                        der Satz sagt es. */}
                    {creating ===
                        entry.project.id && (
                        <span className="mt-2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            wird angelegt …
                        </span>
                    )}
                </span>
            </button>
            {/* Der Weg zum vorhandenen Entwurf,
                statt daneben einen zweiten
                anzulegen. Eigener Knopf und
                nicht im Zeilenknopf: ein
                Schalter im Schalter waere
                weder anklickbar noch
                vorlesbar. */}
            {draft && (
                <button
                    type="button"
                    onClick={() => {
                        closePicker();
                        void openInvoice(draft.id);
                    }}
                    disabled={
                        creating != null ||
                        opening != null ||
                        pickerLocked
                    }
                    className="flex shrink-0 items-center gap-1.5 border-l border-slate-200 px-4 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 disabled:opacity-50"
                >
                    {opening === draft.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <PenLine className="h-4 w-4" />
                    )}
                    Entwurf öffnen
                </button>
            )}
            {/* MONATSPAUSCHALE: der Monatsvorschlag
                im Rechnungsweg. Eigener Knopf neben
                der Zeile (wie „Entwurf öffnen") —
                ein Schalter im Zeilenknopf waere
                weder anklickbar noch vorlesbar.
                Er oeffnet nur den Dialog; gebucht
                wird dort auf Klick, nie hier. */}
            {entry.flatSuggestions.length > 0 && (
                <button
                    type="button"
                    onClick={() =>
                        setFlatBooking({
                            projectId: entry.project.id,
                            projectName:
                                entry.project.name ||
                                entry.project.project_number ||
                                'Projekt',
                            suggestions: entry.flatSuggestions,
                        })
                    }
                    disabled={creating != null || pickerLocked}
                    className="flex shrink-0 items-center gap-1.5 border-l border-slate-200 px-4 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 disabled:opacity-50"
                >
                    <CalendarClock className="h-4 w-4" />
                    <span>
                        {entry.flatSuggestions[0].monthLabel} noch
                        nicht gebucht — buchen
                        {entry.flatSuggestions.length > 1 && (
                            <span className="text-indigo-400">
                                {' '}
                                (+
                                {entry.flatSuggestions.length - 1}
                                )
                            </span>
                        )}
                    </span>
                </button>
            )}
        </li>
    );
}
